import React, { useState, useEffect, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Building2, User, MapPin, Users, Send, MessageSquare, FileText, Download, Eye, File } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getDocuments } from '@/services/uploadService';

export default function LLCFormationDetailView() {
  const { llcId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [llc, setLlc] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Document preview modal state (for agents - no download)
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    documentId: null,
    fileUrl: null,
    fileName: null,
    mimeType: null
  });

  // Get clientId from location state if navigated from client detail page
  const clientId = location.state?.clientId;

  // Fetch uploaded documents from storage
  const fetchDocuments = async () => {
    try {
      const docs = await getDocuments('llc_creation', llcId);
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // View document - agents see modal, admins can open in new tab
  const handleViewDocument = async (documentId, url, fileName = null, mimeType = null) => {
    const backendUrl = getBackendUrl();
    const token = localStorage.getItem('access_token');
    
    if (user?.role === 'admin') {
      // Admin can open in new tab using proxy (can download)
      try {
        const viewUrl = documentId 
          ? `${backendUrl}/api/files/view/${documentId}`
          : (url.startsWith('http') ? url : `${backendUrl}${url}`);
        
        const response = await fetch(viewUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load document');
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } catch (error) {
        console.error('Error viewing document:', error);
        toast.error('Failed to load document');
      }
    } else {
      // Agent sees modal (no download)
      setPreviewModal({
        isOpen: true,
        documentId: documentId,
        fileUrl: url,
        fileName: fileName,
        mimeType: mimeType
      });
    }
  };

  // Download document - only for admins
  const handleDownloadDocument = async (documentId, url, filename) => {
    // Only allow download for admins
    if (user?.role !== 'admin') {
      toast.error('Download not available. Please contact admin.');
      return;
    }
    
    const backendUrl = getBackendUrl();
    const token = localStorage.getItem('access_token');
    
    try {
      const downloadUrl = documentId 
        ? `${backendUrl}/api/files/download/${documentId}`
        : (url.startsWith('http') ? url : `${backendUrl}${url}`);
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // Auto-scroll to latest message within container
  const scrollToBottom = () => {
    // Use parent scroll container instead of scrollIntoView to prevent page scroll
    const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/llc-formations/${llcId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${backendUrl}/api/llc-formations/${llcId}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    if (user && llcId) {
      fetchLLCDetail();
      fetchMessages();
      fetchDocuments();
    }
  }, [user, llcId]);

  // Poll for new messages every 10 seconds when LLC is not DRAFT
  useEffect(() => {
    if (llc && llc.status !== 'DRAFT') {
      const interval = setInterval(() => {
        fetchMessages();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [llc]);

  const fetchLLCDetail = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/llc-formations/${llcId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      
      // Parse JSON fields if they're strings
      if (typeof data.company_info === 'string') {
        try {
          data.company_info = JSON.parse(data.company_info);
        } catch (e) {
          data.company_info = {};
        }
      }
      
      if (typeof data.members === 'string') {
        try {
          data.members = JSON.parse(data.members);
        } catch (e) {
          data.members = {};
        }
      }
      
      if (typeof data.registered_agent === 'string') {
        try {
          data.registered_agent = JSON.parse(data.registered_agent);
        } catch (e) {
          data.registered_agent = null;
        }
      }

      setLlc(data);
    } catch (error) {
      console.error('Error fetching LLC detail:', error);
      toast.error(error.response?.data?.detail || 'Failed to load LLC details');
      setLlc(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (clientId) {
      navigate(`/agent/clients/${clientId}`);
    } else {
      navigate('/agent/llc');
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const payload = { status: newStatus };
      
      // Include status message when setting to NEED_INFO
      if (newStatus === 'NEED_INFO' && statusMessage.trim()) {
        payload.status_message = statusMessage.trim();
      }
      
      const response = await axios.patch(
        `${backendUrl}/api/llc-formations/${llcId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Status updated successfully');
      
      // Refetch the LLC detail to get updated data from server
      await fetchLLCDetail();
      
      // Clear message after successful update
      if (newStatus === 'NEED_INFO') {
        setStatusMessage('');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="LLC Details" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!llc) {
    return (
      <DashboardLayout role={user.role} title="LLC Details" user={user}>
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">LLC formation not found</h3>
          <Button className="mt-4" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="LLC Details" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'Agent', href: '/agent/dashboard' },
          ...(clientId ? [{ label: 'Clients', href: '/agent/clients' }, { label: 'Client Details', href: `/agent/clients/${clientId}` }] : [{ label: 'LLC Formations', href: '/agent/llc' }]),
          { label: 'Details' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">LLC Formation Details</h2>
            <p className="text-muted-foreground">{llc.company_name}</p>
          </div>
          <Button variant="outline" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>

        {/* Status Management */}
        <Card>
          <CardHeader>
            <CardTitle>Status Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="status">Update Status</Label>
                <Select value={llc.status} onValueChange={handleStatusChange}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUBMITTED">Submitted</SelectItem>
                    <SelectItem value="NEED_INFO">Waiting for Info</SelectItem>
                    <SelectItem value="READY_FOR_REVIEW">Ready for Review</SelectItem>
                    <SelectItem value="SUBMITTED_TO_IRS">Submitted to IRS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="current-status">Current Status</Label>
                <div className="mt-2">
                  <Badge className="text-sm">{llc.status}</Badge>
                </div>
              </div>
            </div>

            {llc.status === 'NEED_INFO' && (
              <div>
                <Label htmlFor="status-message">Status Message (for client)</Label>
                <Textarea
                  id="status-message"
                  placeholder="Enter message for client about what info is needed..."
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  rows={3}
                />
                <Button
                  className="mt-2"
                  size="sm"
                  onClick={() => handleStatusChange('NEED_INFO')}
                  disabled={!statusMessage.trim()}
                >
                  Update Message
                </Button>
              </div>
            )}

            {llc.status_message && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-medium text-yellow-800">Current Message:</p>
                <p className="text-sm text-yellow-700 mt-1">{llc.status_message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversation with Client */}
        {llc.status !== 'DRAFT' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Conversation with Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg">
                <ScrollArea className="h-[300px] p-4">
                  <div className="space-y-3">
                    {messages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No messages yet. Start a conversation with the client.
                      </p>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            msg.sender_role === 'agent' ? 'justify-end' : 'justify-start'
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-lg p-3",
                              msg.sender_role === 'agent'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            )}
                          >
                            <p className="text-xs font-medium mb-1 opacity-70">
                              {msg.sender_role === 'agent' ? 'You' : msg.sender_name || 'Client'}
                            </p>
                            <p className="text-sm">{msg.message}</p>
                            <p className="text-xs mt-1 opacity-50">
                              {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      disabled={sendingMessage}
                    />
                    <Button 
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{llc.client_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{user?.role === 'admin' ? (llc.client_email || 'N/A') : '••••••@••••.com'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Company Name</p>
                <p className="font-semibold text-lg">{llc.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">State of Formation</p>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">{llc.state}</p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Business Type</p>
                <p className="font-medium">{llc.business_type || 'LLC'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Submission Date</p>
                <p className="font-medium">
                  {llc.created_at ? new Date(llc.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            {llc.company_info && Object.keys(llc.company_info).length > 0 && (
              <>
                {llc.company_info.selectedPackage && (
                  <div>
                    <p className="text-sm text-muted-foreground">Selected Package</p>
                    <p className="font-medium capitalize">{llc.company_info.selectedPackage}</p>
                  </div>
                )}
                
                {llc.company_info.businessPurpose && (
                  <div>
                    <p className="text-sm text-muted-foreground">Business Purpose</p>
                    <p className="text-sm">{llc.company_info.businessPurpose}</p>
                  </div>
                )}

                {llc.company_info.managementStructure && (
                  <div>
                    <p className="text-sm text-muted-foreground">Management Structure</p>
                    <p className="font-medium capitalize">{llc.company_info.managementStructure.replace('_', ' ')}</p>
                  </div>
                )}

                {(llc.company_info.businessAddress || llc.company_info.businessCity || llc.company_info.businessState || llc.company_info.businessZip) && (
                  <div>
                    <p className="text-sm text-muted-foreground">Business Address</p>
                    <p className="text-sm">
                      {[
                        llc.company_info.businessAddress,
                        llc.company_info.businessCity,
                        llc.company_info.businessState,
                        llc.company_info.businessZip
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}

                {llc.company_info.alternateNames && llc.company_info.alternateNames.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Alternate Names</p>
                    <p className="text-sm">{llc.company_info.alternateNames.join(', ')}</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Members */}
        {llc.members && llc.members.members && llc.members.members.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Members ({llc.members.members.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {llc.members.members.map((member, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{member.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm">{user?.role === 'admin' ? (member.email || 'N/A') : '••••••@••••.com'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Ownership %</p>
                        <p className="text-sm">{member.ownershipPercentage || 'N/A'}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registered Agent */}
        {llc.registered_agent && Object.keys(llc.registered_agent).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Registered Agent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {llc.registered_agent.useOwnAgent !== undefined && (
                  <div>
                    <p className="text-sm text-muted-foreground">Agent Type</p>
                    <p className="font-medium">{llc.registered_agent.useOwnAgent ? 'Own Agent' : 'Professional Service'}</p>
                  </div>
                )}
                {llc.registered_agent.agentName && (
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{llc.registered_agent.agentName}</p>
                  </div>
                )}
                {(llc.registered_agent.agentAddress || llc.registered_agent.agentCity || llc.registered_agent.agentState || llc.registered_agent.agentZip) && (
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="text-sm">
                      {[
                        llc.registered_agent.agentAddress,
                        llc.registered_agent.agentCity,
                        llc.registered_agent.agentState,
                        llc.registered_agent.agentZip
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Documents from Storage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Uploaded Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No documents uploaded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.file_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {doc.file_type && <Badge variant="outline" className="text-xs">{doc.file_type}</Badge>}
                          <span>{doc.file_size && `${(doc.file_size / 1024).toFixed(1)} KB`}</span>
                          <span>{doc.uploaded_at && `• ${new Date(doc.uploaded_at).toLocaleDateString()}`}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDocument(doc.id, doc.file_url, doc.file_name, doc.mime_type)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {user?.role === 'admin' ? 'View' : 'Preview'}
                      </Button>
                      {/* Download only available for admins */}
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadDocument(doc.id, doc.file_url, doc.file_name)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Document Preview Modal - for agents (no download) */}
      <DocumentPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, documentId: null, fileUrl: null, fileName: null, mimeType: null })}
        documentId={previewModal.documentId}
        fileUrl={previewModal.fileUrl}
        fileName={previewModal.fileName}
        mimeType={previewModal.mimeType}
      />
    </DashboardLayout>
  );
}
